/**
 * WHO IS COMING TO THE YARD, WHEN, AND WHAT FOR.
 *
 * Anthony, 2026-09-28: "have a list of customers coming in and when they
 * are coming and for what on the dash". The Rentals side's home is this
 * list, grouped Today, Tomorrow and the next seven days, because the
 * question at the yard gate in the morning is not "what lane is each trip
 * in" (that is the board) but "who is turning up, and do I have their RV
 * ready".
 *
 * FIVE REASONS SOMEBODY IS EXPECTED, and each is its own label so the list
 * reads without opening anything:
 *
 *   Pickup           a booked trip starting, collected at the yard
 *   Return           a trip ending, brought back to the yard
 *   Delivery         a booked trip starting that WE drive out, with the address
 *   Delivery pickup  a delivered trip ending, which we drive out to collect
 *   Repair           an RV booked in on the repair side's schedule
 *
 * The last one is why this is not only a rentals list: an RV yard that
 * also fixes RVs has one gate, and a customer bringing a Class C in for a
 * slide is as much "coming in" as a family picking one up.
 *
 * OVERDUE GOES FIRST, IN ITS OWN GROUP. A trip still Out or Returning after
 * its return day is the one row that needs a phone call rather than a
 * glance, and a list sorted by day would put it at the top anyway, but
 * with nothing to say why.
 *
 * Pure. The caller works out each job's shop day and time label in the
 * shop's zone before it gets here, so nothing below needs a clock or a
 * timezone and the tests can pin "today".
 */

import { addDays, daysUntil, isYmd, type Stage } from "./rental.ts";

export type ArrivalKind = "pickup" | "return" | "delivery" | "delivery_pickup" | "repair";

export const ARRIVAL_LABEL: Record<ArrivalKind, string> = {
  pickup: "Pickup",
  return: "Return",
  delivery: "Delivery",
  delivery_pickup: "Delivery pickup",
  repair: "Repair",
};

export type TripIn = {
  id: string; stage: Stage; start_date: string | null; end_date: string | null;
  renter_name: string | null; renter_phone: string | null;
  delivery: boolean; delivery_address: string | null;
  unit: string | null;
};

export type JobIn = {
  id: string;
  /** The job's day in the SHOP's zone, YYYY-MM-DD. */
  day: string;
  /** "9:30 AM", already in the shop's zone, or "" with no time. */
  time: string;
  name: string | null; phone: string | null;
  complaint: string | null; unit: string | null;
};

export type Arrival = {
  key: string;
  kind: ArrivalKind;
  /** "Pickup", or "Repair: slide will not come in". */
  what: string;
  day: string;
  time: string;
  name: string;
  phone: string | null;
  unit: string;
  /** Where we are driving, for a delivery or a delivery pickup. */
  address: string | null;
  href: string;
  overdue: boolean;
};

export type ArrivalGroups = { overdue: Arrival[]; today: Arrival[]; tomorrow: Arrival[]; week: Arrival[] };

/** How far ahead the list looks, counting today as day 0. */
export const LOOK_AHEAD_DAYS = 7;

const ORDER: Record<ArrivalKind, number> = { return: 0, delivery_pickup: 1, pickup: 2, delivery: 3, repair: 4 };

const sort = (a: Arrival, b: Arrival): number =>
  a.day.localeCompare(b.day) || (a.time && b.time ? a.time.localeCompare(b.time) : 0)
  || ORDER[a.kind] - ORDER[b.kind] || a.name.localeCompare(b.name);

/** Every expected arrival from today to LOOK_AHEAD_DAYS out, grouped, overdue returns first. */
export function arrivals(trips: readonly TripIn[], jobs: readonly JobIn[], today: string): ArrivalGroups {
  const out: ArrivalGroups = { overdue: [], today: [], tomorrow: [], week: [] };
  const last = addDays(today, LOOK_AHEAD_DAYS);
  const inWindow = (d: string) => d >= today && d <= last;
  const place = (a: Arrival) => {
    if (a.overdue) return void out.overdue.push(a);
    const n = daysUntil(today, a.day);
    (n === 0 ? out.today : n === 1 ? out.tomorrow : out.week).push(a);
  };

  for (const t of trips) {
    const name = t.renter_name?.trim() || "No name yet";
    const unit = t.unit?.trim() || "No RV picked";
    const base = { name, phone: t.renter_phone, unit, href: `#/trip/${t.id}`, time: "" };
    if ((t.stage === "booked" || t.stage === "prep") && isYmd(t.start_date) && inWindow(t.start_date)) {
      const kind: ArrivalKind = t.delivery ? "delivery" : "pickup";
      place({ ...base, key: `t:${t.id}:out`, kind, what: ARRIVAL_LABEL[kind], day: t.start_date,
        address: t.delivery ? t.delivery_address : null, overdue: false });
    }
    if ((t.stage === "out" || t.stage === "returning") && isYmd(t.end_date)) {
      const late = t.end_date < today;
      if (!late && !inWindow(t.end_date)) continue;
      const kind: ArrivalKind = t.delivery ? "delivery_pickup" : "return";
      place({ ...base, key: `t:${t.id}:back`, kind, what: late ? `${ARRIVAL_LABEL[kind]}, overdue` : ARRIVAL_LABEL[kind],
        day: t.end_date, address: t.delivery ? t.delivery_address : null, overdue: late });
    }
  }

  for (const j of jobs) {
    if (!isYmd(j.day) || !inWindow(j.day)) continue;
    const said = j.complaint?.replace(/\s+/g, " ").trim().slice(0, 80);
    place({ key: `j:${j.id}`, kind: "repair", what: said ? `Repair: ${said}` : "Repair",
      day: j.day, time: j.time, name: j.name?.trim() || "No customer on the job", phone: j.phone,
      unit: j.unit?.trim() || "", address: null, href: `#/job/${j.id}`, overdue: false });
  }

  out.overdue.sort(sort);
  out.today.sort(sort);
  out.tomorrow.sort(sort);
  out.week.sort(sort);
  return out;
}
